// bm-design-system: testimonial card (carousel style)
//
// A client quote card: a circular profile photo (or a blank image placeholder
// when none is given), a centered editorial-italic quote, the name, and a
// designation. Styled entirely with design tokens + the `.testimonial-quote`
// class (no `.landing-scope`-scoped classes), so it renders correctly on the
// light-locked marketing page and in the themeable admin design system. Fixed
// width so a row of them tiles cleanly inside a TestimonialCarousel; also usable
// standalone.
import * as React from "react";
import { User } from "lucide-react";
import { cn } from "@/lib/utils";

export interface TestimonialProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "role"> {
  /** The quote itself, without surrounding quotation marks. */
  quote: React.ReactNode;
  /** Who said it. */
  name: string;
  /** Their title / company. Omit for an anonymous quote. */
  attribution?: React.ReactNode;
  /** Headshot URL / asset path. Omit to show a blank image placeholder. */
  profileImage?: string;
}

const Testimonial = React.forwardRef<HTMLElement, TestimonialProps>(
  ({ quote, name, attribution, profileImage, className, ...props }, ref) => (
    <figure
      ref={ref}
      className={cn(
        "flex min-h-[20rem] w-80 shrink-0 flex-col items-center justify-center rounded-3xl border border-hairline bg-page p-8 text-center shadow-sm sm:w-96",
        className,
      )}
      {...props}
    >
      {profileImage ? (
        <img
          src={profileImage}
          alt={name}
          width={96}
          height={96}
          loading="lazy"
          className="h-24 w-24 rounded-full border border-hairline object-cover"
        />
      ) : (
        // Blank image placeholder — a neutral avatar until a real headshot exists.
        <span
          aria-hidden="true"
          className="flex h-24 w-24 items-center justify-center rounded-full border border-hairline bg-surface text-ink-muted"
        >
          <User className="h-8 w-8" aria-hidden="true" />
        </span>
      )}

      <blockquote className="testimonial-quote mt-6">{quote}</blockquote>

      <figcaption className="mt-6">
        <strong className="block">{name}</strong>
        {attribution && (
          <span className="mt-0.5 block text-sm text-ink-muted">
            {attribution}
          </span>
        )}
      </figcaption>
    </figure>
  ),
);
Testimonial.displayName = "Testimonial";

export { Testimonial };
